/**
 * ViewSettings configuration constants
 */

import {
  TableOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  ProjectOutlined,
  FundOutlined,
  LayoutOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import React from 'react';

// Properties overlay components (inline content)
import TableConfigOverlay from '../components/Records/Properties/Overlay';
import CardsConfigOverlay from '../components/Cards/Header/Config/Overlay';
import CalendarConfigOverlay from '../components/Calendar/Header/Config/Overlay';
import GanttConfigOverlay from '../components/Gantt/Config/Overlay';

// Extra components
import ReportsExtra from '../components/Reports/Header/BoardViewMode';
import HistoryFilters from '../components/History/HistoryFilters';

export const SCOPES = {
  GLOBAL: 'global',
  PERSONAL: 'personal',
};

export const VIEW_MODES = {
  TABLE: 'table',
  CARDS: 'cards',
  CALENDAR: 'calendar',
  GANTT: 'gantt',
  REPORTS: 'reports',
  PIVOT: 'pivot',
  COUNT: 'count',
  HISTORY: 'history',
  MANUAL: 'manual',
  RECORD_HISTORY: 'recordHistory',
};

export const EXCLUDED_VIEW_MODES = {
  MANUAL: 'manual',
  RECORD_HISTORY: 'recordHistory',
};

const GanttIcon = (props) => <ProjectOutlined {...props} rotate={90} />;

export const VIEW_MODE_ICONS = {
  [VIEW_MODES.TABLE]: TableOutlined,
  [VIEW_MODES.CARDS]: AppstoreOutlined,
  [VIEW_MODES.CALENDAR]: CalendarOutlined,
  [VIEW_MODES.GANTT]: GanttIcon,
  [VIEW_MODES.REPORTS]: FundOutlined,
  [VIEW_MODES.PIVOT]: LayoutOutlined,
  [VIEW_MODES.COUNT]: CalculatorOutlined,
  [VIEW_MODES.HISTORY]: HistoryOutlined,
  [VIEW_MODES.MANUAL]: InfoCircleOutlined,
};

export const VIEW_MODE_COLORS = {
  [VIEW_MODES.TABLE]: '#429fc3',
  [VIEW_MODES.CARDS]: '#429fc3',
  [VIEW_MODES.CALENDAR]: '#28bb5d',
  [VIEW_MODES.GANTT]: '#28bb5d',
  [VIEW_MODES.REPORTS]: '#c79e26',
  [VIEW_MODES.PIVOT]: '#c79e26',
  [VIEW_MODES.COUNT]: '#c79e26',
  [VIEW_MODES.HISTORY]: '#c27fcd',
  [VIEW_MODES.MANUAL]: '#9ea1a7',
};

export const SETTINGS_KEYS = {
  // Common keys
  WIDTH: 'width',
  VISIBLE: 'visible',
  PINNED: 'pinned',
  COLOR: 'color',
  FIELDS_ORDER: 'fieldsOrder',
  SORTING_RECORDS: 'sortingRecords',
  SORT_TYPE: 'sortType',
  SORT_FIELD: 'sortField',
  FIELD_ID: 'fieldId',
  TIMER: 'timer',
  FIELDS: 'fields',
  GROUPS: 'groups',
  ROW_HEIGHT: 'rowHeight',

  // Cards
  AXIS_X: 'axisX',
  AXIS_Y: 'axisY',
  COLUMN_WIDTH_FIXED: 'columnWidthFixed',
  ROW_HEIGHT_DATA: 'rowHeightData',
  COLUMN_WIDTH_DATA: 'columnWidthData',
  LABEL_COLUMN_WIDTH: 'labelColumnWidth',

  // Calendar
  CALENDAR_START: 'start',
  CALENDAR_END: 'end',
  SPLIT: 'split',
  SUGGEST_RECORDS: 'suggestRecords',
  CALENDAR_VIEW_TYPE: 'calendarViewType',

  // GANTT
  GANTT_START: 'start',
  GANTT_END: 'end',
  GANTT_TITLE: 'title',
  GANTT_EMPLOYEE: 'employee',
  GANTT_SCALE: 'scale',
  GANTT_PROGRESS: 'progress',
  GANTT_GRID_WIDTH: 'gridWidth',
  GANTT_DISPLAY_MODE: 'displayMode',
  GANTT_COLUMN_WIDTHS: 'columnWidths',
  GANTT_LINK_FIELD: 'linkField',
  GANTT_LINK_DIRECTION: 'linkDirection',

  // Pivot
  PIVOT_COLS: 'cols',
  PIVOT_ROWS: 'rows',
  PIVOT_VALS: 'vals',
  PIVOT_FN: 'fn',
  PIVOT_HEAT_MAP: 'heatMap',

  // Other keys
  DEFAULT_VIEW_MODE: 'defaultViewMode',
};

// Конвертация виртуальных полей: клиент → внутренний формат.
// Системная колонка ID использует ключ "#id" напрямую, поэтому
// маппинг "id" → "#id" здесь не нужен и создавал коллизию с кастомными
// полями, у которых пользователь задал id = "id".
export const VIRTUAL_FIELDS = {
  // createdAt: '#createdAt', // if will be needed
  // updatedAt: '#updatedAt', // if will be needed
};

// Обратная конвертация: внутренний формат (с #) → API записей (без #).
// Задаётся вручную, т.к. VIRTUAL_FIELDS больше не содержит прямого маппинга.
export const VIRTUAL_FIELDS_REVERSE = {
  '#id': 'id',
};

// Определяет, что отображается для каждого режима отображения
export const VIEW_MODES_CONFIG = {
  [VIEW_MODES.TABLE]: {
    filters: true,
    fields: true,
    properties: TableConfigOverlay,
    extra: null,
    search: true,
  },
  [VIEW_MODES.CARDS]: {
    filters: true,
    fields: true,
    fieldsDefaultVisible: false,
    properties: CardsConfigOverlay,
    extra: null,
    search: true,
  },
  [VIEW_MODES.CALENDAR]: {
    filters: true,
    fields: true,
    fieldsDefaultVisible: false,
    properties: CalendarConfigOverlay,
    extra: null,
    search: true,
  },
  [VIEW_MODES.GANTT]: {
    filters: true,
    fieldsDefaultVisible: false,
    extra: null,
    properties: GanttConfigOverlay,
    search: true,
  },
  [VIEW_MODES.REPORTS]: {
    filters: true,
    fields: false,
    properties: null,
    extra: ReportsExtra,
    search: true,
  },
  [VIEW_MODES.PIVOT]: {
    filters: true,
    fields: false,
    properties: null,
    extra: null,
    search: true,
  },
  [VIEW_MODES.COUNT]: {
    filters: true,
    fields: false,
    properties: null,
    extra: null,
    search: true,
  },
  [VIEW_MODES.HISTORY]: {
    filters: true,
    fields: true,
    properties: null,
    extra: HistoryFilters,
    search: false,
  },
  [VIEW_MODES.MANUAL]: {
    filters: false,
    fields: false,
    properties: null,
    extra: null,
    search: false,
  },
};

export const LEFT_PANEL_TABS = {
  VIEWS: 'views',
  FILTERS: 'filters',
  FIELDS: 'fields', // Поля
  CONTROL: 'control', // Раскладка
};

export const VIRTUAL_VIEW_IDS = ['0', '$new'];

export function resolveDefaultViewId(views, viewsLoaded, storedViewId) {
  if (!storedViewId) return '0';
  if (VIRTUAL_VIEW_IDS.includes(storedViewId)) return storedViewId;
  if (!viewsLoaded) return null;
  return views && views.get(storedViewId) ? storedViewId : '0';
}

export function isTabVisible(tabId, viewMode) {
  const config = viewMode && VIEW_MODES_CONFIG[viewMode];
  if (tabId === LEFT_PANEL_TABS.VIEWS) return true;
  if (tabId === LEFT_PANEL_TABS.FILTERS) return !config || !!config.filters;
  if (tabId === LEFT_PANEL_TABS.FIELDS) return !!(config && config.fields);
  if (tabId === LEFT_PANEL_TABS.CONTROL) return !!(config && config.properties);
  return false;
}

export default {
  SCOPES,
  VIEW_MODES,
  VIEW_MODE_ICONS,
  VIEW_MODE_COLORS,
  SETTINGS_KEYS,
  VIRTUAL_FIELDS,
  VIRTUAL_FIELDS_REVERSE,
  VIEW_MODES_CONFIG,
  LEFT_PANEL_TABS,
};
